import { EstimationMethodCalculationsTab } from "#batteries-included-components/Tabs/EstimationMethod/CalculationsTab";
import { ConfigurationTab } from "#batteries-included-components/Tabs/EstimationMethod/ConfigurationTab/ConfigurationTab";
import { EstimationMethodOverviewTab } from "#batteries-included-components/Tabs/EstimationMethod/OverviewTab";
import { EstimationMethodRecordsTab } from "#batteries-included-components/Tabs/EstimationMethod/RecordsTab";
import { useBreadcrumbs } from "#routers/breadcrumbsHelper";
import { useNavigate, useParams, useSearchParams } from "#routers/hooks";
import { linkToFacilities } from "#src/Routers/links";
import { EstimationMethodEditDialog } from "#src/batteries-included-components/Dialogs/EstimationMethodEditDialog";
import type { EstimationMethodDetailViewFilterType } from "#src/batteries-included-components/FilterAreas/EstimationMethodsFilterAreas/EstimationMethodsFilterAreas";
import { AccessDeniedLayout } from "#src/batteries-included-components/Layouts/Authorization/AccessDenied";
import { IsFeatureAvailable } from "#src/batteries-included-components/Layouts/Authorization/IsFeatureAvailable";
import { useGetOneEquipment } from "#src/components/hooks/adapters/useEquipment";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import { useDeleteEstimationMethod } from "#src/components/hooks/useEstimationMethod";
import { MUTATION_STATUS } from "#src/constants";
import useLocalization from "#src/hooks/useLocalization";
import { useStickyState } from "#src/hooks/useStickyState";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { ORGANIZATION_BREADCRUMB } from "#src/routes/organization";
import { EQUIPMENT_LIST_BREADCRUMB } from "#src/routes/organization/equipment";
import {
  EQUIPMENT_DETAIL_BREADCRUMB,
  linkToEquipmentDetail,
} from "#src/routes/organization/equipment/[equipmentId]";
import { ESTIMATION_METHOD_LIST_BREADCRUMB } from "#src/routes/organization/equipment/[equipmentId]/estimation-method";
import { linkToEstimationMethodCalculationResult } from "#src/routes/organization/equipment/[equipmentId]/estimation-method/[methodId]/result/[resultId]/detail";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Dialog,
  generateDocumentTitle,
  Link,
  Page,
  Pill,
  Tab,
} from "@validereinc/common-components";
import {
  AssetType,
  CalculatorDomain,
  EstimationMethodAdapter,
  ReportingGroupAdapter,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import { toTitleCaseString, yearMonthFormatter } from "@validereinc/utilities";
import parseISO from "date-fns/parseISO";
import React, { useState } from "react";
import { Titled } from "react-titled";
import { ESTIMATION_METHOD_DETAIL_BREADCRUMB } from "./";

export const EstimationMethodDetailPage = ({
  equipmentId,
  methodId,
}: {
  equipmentId: string;
  methodId: string;
}) => {
  const [viewConfigStorageKey, tableStateStorageKey] = useStorageKey(
    ["view-configuration", "table-state"],
    "estimation-method-overview"
  );
  const [viewFilters] = useStickyState<EstimationMethodDetailViewFilterType>(
    {
      month: {},
    },
    viewConfigStorageKey
  );

  const period = viewFilters.month?.from
    ? yearMonthFormatter(parseISO(viewFilters.month.from))
    : undefined;

  const { data: equipmentDetail, isLoading: isEquipmentLoading } =
    useGetOneEquipment(equipmentId, { period }, { enabled: !!equipmentId });
  const methodDetailsQuery = useQuery({
    queryKey: [
      Resources.ESTIMATION_METHOD,
      methodId,
      { assetType: AssetType.EQUIPMENT, period },
    ],
    queryFn: () => {
      return EstimationMethodAdapter.getOne({
        id: methodId,
        meta: {
          entityType: AssetType.EQUIPMENT,
          period,
        },
      });
    },
    select: (resp) => resp.data,
    enabled: !!methodId,
  });
  const calculatorDetailsQuery = useQuery({
    queryKey: [
      Resources.CALCULATOR,
      methodDetailsQuery.data?.analytics_calculator_id,
    ],
    queryFn: () => {
      if (!methodDetailsQuery.data?.analytics_calculator_id) return null;

      return CalculatorDomain.getCalculator({
        calculatorId: methodDetailsQuery.data.analytics_calculator_id,
      });
    },
    enabled: !!methodDetailsQuery.data?.analytics_calculator_id,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const { localize, isLoading: isMappingLoading } = useLocalization();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteEstimationMethod, deleteEstimationMethodStatus] =
    useDeleteEstimationMethod();
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const reportingGroupsQuery = useQuery({
    queryKey: ["reportingGroups"],
    queryFn: () => ReportingGroupAdapter.getList({}),
  });
  const reportingGroups = reportingGroupsQuery?.data?.data ?? [];
  const calculatorVersion = calculatorDetailsQuery.data?.versions.find(
    ({ version }) => version === calculatorDetailsQuery.data?.default_version
  );

  // callbacks
  const handleActiveTabKeyChange = (tabKey: string) => {
    setSearchParams({ ...searchParams, tab: tabKey });
  };

  const handleDelete = () => {
    if (!methodDetailsQuery.data?.id) {
      return;
    }

    deleteEstimationMethod(methodDetailsQuery.data.id, AssetType.EQUIPMENT);
    navigate({
      pathname: linkToEquipmentDetail(equipmentDetail?.id ?? equipmentId),
      query: {
        tab: "estimationMethods",
      },
    });
  };

  // effects & hooks
  const navigate = useNavigate();

  const createInfo = useActionDetails({
    type: "create",
    date: methodDetailsQuery.data?.created_at,
    byUserId: methodDetailsQuery.data?.created_by,
  });

  const editInfo = useActionDetails({
    type: "edit",
    date: methodDetailsQuery.data?.updated_at,
    byUserId: methodDetailsQuery.data?.updated_by,
  });

  // computed
  const breadcrumbs = useBreadcrumbs(
    [
      ORGANIZATION_BREADCRUMB,
      EQUIPMENT_LIST_BREADCRUMB,
      EQUIPMENT_DETAIL_BREADCRUMB,
      ESTIMATION_METHOD_LIST_BREADCRUMB,
      ESTIMATION_METHOD_DETAIL_BREADCRUMB,
    ],
    {
      2: equipmentDetail?.name,
      4: methodDetailsQuery.data?.name,
    }
  );

  const hasReportingGroups =
    methodDetailsQuery.data?.reporting_groups &&
    methodDetailsQuery.data.reporting_groups.length > 0;

  const detailsPanelData = [
    {
      title: "Reporting Scenarios",
      value: !hasReportingGroups ? null : (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            flexWrap: "wrap",
            gap: "4px",
          }}
        >
          {methodDetailsQuery.data?.reporting_groups?.map((group) => (
            <Pill
              key={group.name}
              variant="primary"
              isBordered
              hasDot={false}
            >
              {group.name.trim()}
            </Pill>
          ))}
        </div>
      ),
      description: hasReportingGroups
        ? undefined
        : "Edit this estimation method to associate reporting scenarios",
    },
    {
      title: localize("Equipment"),
      value: (
        <Link
          onClick={() => {
            navigate({
              pathname: linkToEquipmentDetail(equipmentDetail?.id),
            });
          }}
          label={equipmentDetail?.name ?? "-"}
        />
      ),
    },
    {
      title: localize("Facility"),
      value: (
        <Link
          onClick={() => {
            navigate({
              pathname: linkToFacilities(equipmentDetail?.facility_id),
            });
          }}
          label={equipmentDetail?.facility?.name ?? "-"}
        />
      ),
    },
    {
      title: `${localize("Equipment")} Type`,
      value: equipmentDetail?.type.name,
    },
    {
      title: "Library",
      value:
        methodDetailsQuery.data?.analytics_library_id ??
        calculatorDetailsQuery.data?.library,
    },
    {
      title: "Calculator Name",
      value: calculatorVersion?.title,
    },
  ];

  const pageActionRow = (
    <>
      <Button
        title="Delete"
        variant="error-outline"
        onClick={() => setIsDeleteModalOpen(true)}
        isLoading={deleteEstimationMethodStatus === MUTATION_STATUS.LOADING}
        disabled={
          !methodDetailsQuery.data?.id || !methodDetailsQuery.data?.name
        }
      >
        Delete
      </Button>
      <Button
        variant="outline"
        onClick={() => setIsEditDialogOpen(true)}
        isLoading={deleteEstimationMethodStatus === MUTATION_STATUS.LOADING}
        disabled={
          !methodDetailsQuery.data?.id || !methodDetailsQuery.data?.name
        }
      >
        Edit
      </Button>
    </>
  );

  return (
    <>
      <Page
        category="Estimation Method"
        title={
          methodDetailsQuery.data ? (
            <>
              {methodDetailsQuery.data?.name}&nbsp;
              <Pill
                variant={
                  methodDetailsQuery.data.status === "active"
                    ? "success"
                    : "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(methodDetailsQuery.data?.status ?? "-", {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            ""
          )
        }
        isLoading={
          methodDetailsQuery.isLoading ||
          calculatorDetailsQuery.isLoading ||
          isEquipmentLoading
        }
        breadcrumbs={breadcrumbs}
        onActiveTabKeyChange={handleActiveTabKeyChange}
        activeTabKey={searchParams?.tab ?? "overview"}
        actionRow={pageActionRow}
        renderMeta={
          !editInfo.description
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments
                  values={[editInfo.description, createInfo.description]}
                />
              )
        }
      >
        <Tab
          tabKey="overview"
          title="Overview"
        >
          <Titled
            title={generateDocumentTitle(
              breadcrumbs.at(-1)?.title ??
                ESTIMATION_METHOD_DETAIL_BREADCRUMB.title,
              breadcrumbs.at(-4)?.title
            )}
          >
            <EstimationMethodOverviewTab
              detailsPanelProps={{
                data: detailsPanelData,
                isLoading: isMappingLoading,
              }}
              methodId={methodId}
              assetType={AssetType.EQUIPMENT}
              period={period}
            />
          </Titled>
        </Tab>
        <Tab
          tabKey="configuration"
          title="Configuration"
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ??
                ESTIMATION_METHOD_DETAIL_BREADCRUMB.title
              } Configuration`,
              breadcrumbs.at(-4)?.title
            )}
          >
            <ConfigurationTab
              viewConfigStorageKey={viewConfigStorageKey}
              tableStateStorageKey={tableStateStorageKey}
              methodId={methodId}
              assetType={AssetType.EQUIPMENT}
              period={period}
            />
          </Titled>
        </Tab>
        <Tab
          tabKey={"calculations"}
          title="Calculations"
        >
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ??
                ESTIMATION_METHOD_DETAIL_BREADCRUMB.title
              } ${ResourceDefinitions.estimation_method_config.label.plural}`,
              breadcrumbs.at(-4)?.title
            )}
          >
            <EstimationMethodCalculationsTab
              methodId={methodId}
              assetId={equipmentId}
              assetType={AssetType.EQUIPMENT}
              onClickSavedResultRecord={(methodId, entityId, yearMonth) =>
                navigate({
                  pathname: linkToEstimationMethodCalculationResult(
                    methodId,
                    entityId,
                    yearMonth
                  ),
                })
              }
            />
          </Titled>
        </Tab>
        <Tab
          tabKey="results"
          title="Results"
        >
          {" "}
          <Titled
            title={generateDocumentTitle(
              `${
                breadcrumbs.at(-1)?.title ??
                ESTIMATION_METHOD_DETAIL_BREADCRUMB.title
              } ${ResourceDefinitions.calculator_result.label.plural}`,
              breadcrumbs.at(-4)?.title
            )}
          >
            <EstimationMethodRecordsTab
              methodId={methodId}
              assetId={equipmentId}
              assetType={AssetType.EQUIPMENT}
              onClickRow={(methodId, entityId, yearMonth) =>
                navigate({
                  pathname: linkToEstimationMethodCalculationResult(
                    methodId,
                    entityId,
                    yearMonth
                  ),
                })
              }
            />
          </Titled>
        </Tab>
      </Page>
      <Dialog
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title={`Delete "${methodDetailsQuery.data?.name ?? "this estimation method"}"`}
        actionRow={[
          <Button
            key="delete-dialog-action"
            onClick={handleDelete}
            variant="error"
          >
            Delete
          </Button>,
        ]}
      >
        <div>
          Are you sure you want to delete this estimation method? This action
          cannot be undone.
        </div>
      </Dialog>

      <EstimationMethodEditDialog
        reportingGroups={reportingGroups}
        method={methodDetailsQuery.data}
        entityType={AssetType.EQUIPMENT}
        onClose={() => setIsEditDialogOpen(false)}
        onSubmit={() => {
          methodDetailsQuery.refetch();
        }}
        isEditDialogOpen={isEditDialogOpen}
      />
    </>
  );
};

export const EstimationMethodDetail = () => {
  const { equipmentId, methodId } = useParams<{
    equipmentId: string;
    methodId: string;
  }>();

  return (
    <IsFeatureAvailable
      fallbackChildren={<AccessDeniedLayout />}
      featureFlagQuery="core:estimation_methods"
      permissionQuery="calculator_results:read"
    >
      <EstimationMethodDetailPage
        equipmentId={equipmentId}
        methodId={methodId}
      />
    </IsFeatureAvailable>
  );
};
